import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "./SimilarScreenModal.module.scss";
import { motion, AnimatePresence } from "framer-motion";
import { IMAGE_SEARCH_STORAGE_KEY } from "@/Views/ImageSearch/ImageSearchModal/ImageSearchModal";

// Local storage key for similar screen data
export const SIMILAR_SCREEN_STORAGE_KEY = "similarScreenData";

const getDataFromStorage = (key: string) => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error(`Error retrieving data from localStorage (${key}):`, error);
    return null;
  }
};

interface SimilarScreenModalProps {
  type: "similar" | "search";
  count: number;
}

const SimilarScreenModal: React.FC<SimilarScreenModalProps> = ({
  type = "similar",
  count,
}) => {
  const isSimilarType = type === "similar";

  const screenData = isSimilarType
    ? getDataFromStorage(SIMILAR_SCREEN_STORAGE_KEY)
    : getDataFromStorage(IMAGE_SEARCH_STORAGE_KEY);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [showImage, setShowImage] = useState(false);

  // Calculate position once on mount and when container changes
  const calculatePosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setModalPosition({
        top: rect.top,
        left: rect.left,
      });
    }
  }, []);

  useEffect(() => {
    calculatePosition();
    window.addEventListener("resize", calculatePosition);
    return () => window.removeEventListener("resize", calculatePosition);
  }, [calculatePosition]);

  const handleOpenModal = () => {
    calculatePosition();
    setOpenModal(true);

    setTimeout(() => {
      setShowImage(true);
    }, 500);
  };

  const handleCloseModal = () => {
    setShowImage(false);
    setOpenModal(false);
  };

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        handleCloseModal();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={containerRef} className={styles.similarScreenContainer}>
      <button
        ref={buttonRef}
        className={styles.similarScreenModalButton}
        onClick={handleOpenModal}
      >
        <img
          className={styles.similarScreenModalButtonImage}
          src={isSimilarType ? screenData?.screen : screenData?.image}
          alt={
            isSimilarType
              ? `${screenData?.appName} screen`
              : "Image search screen"
          }
          loading="eager"
        />
        <div className={styles.similarScreenModalButtonOverlay} />
        <img
          src="/img/SearchIcon.svg"
          alt="search"
          className={styles.similarScreenModalButtonSearchIcon}
        />
      </button>
      <div>
        <h3 className={styles.similarScreenText}>
          {isSimilarType
            ? "Showing similar results for this screen"
            : "Image search"}
        </h3>
        <p className={styles.similarScreenTextResults}>
          {count > 1 ? `${count} results` : `${count} result`}
        </p>
      </div>

      <AnimatePresence>
        {openModal && (
          <motion.div
            className={styles.modalContainer}
            ref={modalRef}
            style={{
              top: `${modalPosition.top}px`,
              left: `${modalPosition.left}px`,
              transformOrigin: "top left",
              willChange: "transform, opacity",
            }}
            initial={{
              opacity: 0,
              scale: 0.2,
              width: "493px",
            }}
            animate={{
              opacity: 1,
              scale: 1,
              width: "493px",
            }}
            exit={{
              opacity: 0,
              scale: 0.2,
              width: "493px",
            }}
            transition={{
              duration: 0.2,
              ease: [0.25, 0.1, 0.25, 1],
            }}
          >
            <div className={styles.modalContent}>
              <div className={styles.modalContentHeader}>
                <img
                  src="/img/SearchIcon.svg"
                  alt="search"
                  className={styles.modalContentHeaderSearch}
                />
                <div className={styles.modalContentHeaderImage}>
                  {showImage ? (
                    <img
                      src={
                        isSimilarType ? screenData?.screen : screenData?.image
                      }
                      alt={
                        isSimilarType
                          ? screenData?.appName
                          : "Image search screen"
                      }
                      loading="eager"
                    />
                  ) : (
                    <div className={styles.skeletonThumbnail} />
                  )}
                </div>
                <h3 className={styles.similarScreenText}>
                  {isSimilarType
                    ? "Showing similar results for this screen"
                    : "Image search"}
                </h3>
              </div>
              <div className={styles.modalContentBody}>
                {isSimilarType && (
                  <motion.div
                    className={`${styles.modalContentBodyHeaderWrapper}`}
                    initial={{ width: "224px" }}
                    animate={{ width: "224px" }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    <div className={styles.modalContentBodyHeader}>
                      {showImage ? (
                        <img
                          src={screenData?.appIcon}
                          alt={`${screenData?.appName} icon`}
                          loading="eager"
                        />
                      ) : (
                        <div className={styles.skeletonAppIcon} />
                      )}
                      <div className={styles.modalContentBodyHeaderTitle}>
                        <h5>{screenData?.appName}</h5>
                        <h6>{screenData?.appDescription}</h6>
                      </div>
                    </div>
                  </motion.div>
                )}
                <div className={styles.modalContentBodyContent}>
                  {showImage ? (
                    <img
                      src={
                        isSimilarType ? screenData?.screen : screenData?.image
                      }
                      alt={
                        isSimilarType
                          ? `${screenData?.appName} screen`
                          : "Image search screen"
                      }
                      loading="eager"
                      width="224"
                      height="485"
                    />
                  ) : (
                    <div className={styles.skeletonImage} />
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SimilarScreenModal;
